<template>
    <div class="mppt" :class="[code, { 'mppt--active' : active }]">
        <img v-if="$resize && $mq.above(799)" class="mppt-image" :src="background">
        <img v-else class="mppt-image" :src="backgroundMobile">

        <button class="mppt-button small semi" :class="{ 'mppt-button-disabled': disabled }" @click="$emit('activated')">MPPT {{ id }}</button>

        <div v-if="panel && dcus > 0" class="mppt-dcus">
            <svg viewBox="0 0 57 48">
                <path d="M14.4845713,1 L1,14.4613493 L1,38.6357659 L9.4194903,47 L42.624734,47 L56,33.6056857 L56,9.52518618 L47.4746938,1 L14.4845713,1 Z" fill="none" stroke="currentColor" stroke-width="3" transform="translate(28.500000, 24.000000) rotate(-180.000000) translate(-28.500000, -24.000000)" />
            </svg>
            <span class="black-dot small semi">{{ dcus }}</span>
        </div>

        <div v-if="panel && multiplier" class="mppt-multiplier black-dot small semi">{{ multiplier }}</div>
        
        <div v-if="panel" class="mppt-panel">
            <p class="small semi">{{ panel.model }}</p>
            <p v-if="!dcus" class="mppt-voltage small semi text--grey-light">
                PVAMV: {{ maxvString }} <span>/</span> Isc: {{ isc }}
            </p>
            <Tooltip v-if="hasFlags" text="Configuration Advice" class="mppt-tooltip">
                <svg class="has-errors"><use xlink:href="#alert" /></svg>
            </Tooltip>
            <Tooltip v-else text="PVAMV is the PV Array’s Maximum Voltage including temperature de-rating. Isc is not adjusted by the 1.25 multiplier. This tool does not support voltage calculations for Bi-facial modules (KI)." class="mppt-tooltip" />
        </div>

        <!-- Tick icon -->
        <div class="mppt-status" :class="{ 'mppt-status--complete': valid && panel }" @click="$emit('activated')">
            <svg viewBox="0 0 32 32">
                <path v-if="panel" d="M28.998 8.531l-2.134-2.134c-0.394-0.393-1.030-0.393-1.423 0l-12.795 12.795-6.086-6.13c-0.393-0.393-1.029-0.393-1.423 0l-2.134 2.134c-0.393 0.394-0.393 1.030 0 1.423l8.924 8.984c0.393 0.393 1.030 0.393 1.423 0l15.648-15.649c0.393-0.392 0.393-1.030 0-1.423z" />
                <path v-else d="M19.587 16.001l6.096 6.096c0.396 0.396 0.396 1.039 0 1.435l-2.151 2.151c-0.396 0.396-1.038 0.396-1.435 0l-6.097-6.096-6.097 6.096c-0.396 0.396-1.038 0.396-1.434 0l-2.152-2.151c-0.396-0.396-0.396-1.038 0-1.435l6.097-6.096-6.097-6.097c-0.396-0.396-0.396-1.039 0-1.435l2.153-2.151c0.396-0.396 1.038-0.396 1.434 0l6.096 6.097 6.097-6.097c0.396-0.396 1.038-0.396 1.435 0l2.151 2.152c0.396 0.396 0.396 1.038 0 1.435l-6.096 6.096z" />
            </svg>
        </div>
    </div>
</template>

<script>
import { get, round } from 'lodash';
import Tooltip from '@/components/Tooltip';

export default {
    components: {
        Tooltip,
    },

    props: {
        panel: {
            type: Object,
            default: null
        },
        inverter: {
            type: Object,
            default: null
        },
        index: {
            type: Number,
            required: true
        },
        id: {
            type: [Number, String],
            required: true
        },
        systemId: {
            type: [Number, String],
            required: true
        },
        parallel: {
            type: [Number, String],
            default: null
        },
        series: {
            type: [Number, String],
            default: null
        },
        dcus: {
            type: [Number, String],
            default: null
        },
        active: {
            type: Boolean,
            required: true
        },
        temperature: {
            type: Object,
            default: () => ({
                min: 0,
                max: 0
            })
        },
        valid: {
            type: Boolean,
            default: false
        },
        hasFlags: {
            type: Boolean,
            default: false
        },
        disabled: {
            type: Boolean,
            default: false
        }
    },

    computed: {
        code() {
            return `mppt-${this.index + 1}`;
        },

        background() {
            const active = this.active ? 'orange' : 'grey';
            return `/dist/images/job/${this.code}-${active}.svg`;
        },

        backgroundMobile() {
            const active = this.active ? 'orange' : 'grey';
            return `/dist/images/job/${this.code}-${active}-mobile.svg`;
        },

        multiplier() {
            return this.parallel * this.series;
        },

        maxv() {
            const { details: { voc, vDeratingCelsius } } = this.panel;
            const pvMaxVoltage = Number(voc) + ((this.temperature.min - 25)  * (Number(voc) * Number(vDeratingCelsius) / 100));
            return Math.round(pvMaxVoltage * this.series);
        },

        maxvString() {
            return this.maxv ? `${this.maxv}V` : 'Error';
        },

        isc() {
            const value = get(this.panel, 'details.panelIsc', false);
            return value ? `${round(value * this.parallel, 2)}A` : 'Error';
        }
    }
};
</script>

<style lang="scss" scoped>
    .mppt {
        position: absolute;
    }

    .mppt--active {
        z-index: 1;

        .mppt-button {
            border-color: currentColor;
            color: #EF6E0B
        }

        .mppt-dcus {
            color: #EF6E0B;
        }
    }

    .mppt-button {
        position: absolute;
        width: 95px;
        height: 40px;
        z-index: 10;
        background-color: #FFF;
        text-align: center;
        outline: none;
        border: 2px solid #DDDDDD;
        border-radius: 999px;
    }

    .mppt-button-disabled {
        cursor: not-allowed;
    }

    .mppt-status {
        position: absolute;
        width: 44px;
        height: 44px;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: #DDDDDD;
        border-radius: 100%;
        border: 2px solid white;
        cursor: pointer;

        svg {
            width: 50%;
            fill: white;
        }
    }

        .mppt-status--complete {
            background-color: #EF6E0B;
        }

    .black-dot {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 40px;
        height: 40px;
        background-color: #414042;
        color: #FFF;
        border-radius: 100%;
    }

    .mppt-multiplier {
        position: absolute;
    }

    .mppt-dcus {
        position: absolute;
        width: 57px;
        height: 48px;
        color: #F2F2F2;

        .black-dot {
            position: absolute;
            top: 4px;
            left: 8px;
        }
    }

    .mppt-panel {
        position: absolute;
        white-space: nowrap;
        line-height: 1.25;

        @media screen and (max-width: 799px) {
            width: 120px;
            white-space: normal;
        }
    }

    .mppt-tooltip {
        position: absolute;
        right: -30px;
        bottom: 1px;

        @media screen and (max-width: 799px) {
            right: -10px;
        }

        &::v-deep .tool-hover {
            line-height: 0;
        }

        &::v-deep .tool-tip {
            right: -3px;

            @media screen and (max-width: 799px) {
                right: -25px;
            }

            &::after {
                right: 4px;

                @media screen and (max-width: 799px) {
                    right: 25px;
                }
            }
        }
    }

    .mppt-voltage {
        margin-top: 0.25em;

        span {
            @media screen and (max-width: 799px) {
                display: block;
                height: 0;
                opacity: 0;
            }
        }
    }

    // MPPT 1
    .mppt-1 {
        top: 0;
        left: 60px;

        @media screen and (max-width: 799px) {
            top: 59px;
            left: -10px;
        }

        .mppt-image {
            @media screen and (max-width: 799px) {
                width: 81px;
                height: 483px;
            }
        }

        .mppt-button {
            top: 9px;
            left: 195px;

            @media screen and (max-width: 799px) {
                top: 240px;
                left: -5px;
            }
        }

        .mppt-status {
            top: 6px;
            left: 132px;

            @media screen and (max-width: 799px) {
                top: 150px;
                left: 19px;
            }
        }

        .mppt-multiplier {
            top: 10px;
            left: 415px;

            @media screen and (max-width: 799px) {
                top: 433px;
                left: 19px;
            }
        }

        .mppt-dcus {
            top: 4px;
            left: 340px;

            @media screen and (max-width: 799px) {
                top: 365px;
                left: 12px;
            }
        }

        .mppt-panel {
            top: 69px;
            left: 412px;

            @media screen and (max-width: 799px) {
                top: 490px;
                left: 0;
            }
        }
    }

    // MPPT 2
    .mppt-2 {
        top: 24px;
        left: 40px;

        @media screen and (max-width: 799px) {
            top: 60px;
            left: 26px;
        }

        .mppt-image {
            @media screen and (max-width: 799px) {
                width: 237px;
                height: 483px;
            }
        }

        .mppt-button {
            top: 119px;
            left: 215px;

            @media screen and (max-width: 799px) {
                top: 240px;
                left: 150px;
            }
        }

        .mppt-status {
            top: 117px;
            left: 152px;

            @media screen and (max-width: 799px) {
                top: 150px;
                left: 174px;
            }
        }

        .mppt-multiplier {
            top: 119px;
            left: 434px;

            @media screen and (max-width: 799px) {
                top: 433px;
                left: 174px;
            }
        }

        .mppt-dcus {
            top: 114px;
            left: 360px;

            @media screen and (max-width: 799px) {
                top: 365px;
                left: 167px;
            }
        }

        .mppt-panel {
            top: 178px;
            left: 432px;

            @media screen and (max-width: 799px) {
                top: 490px;
                left: 157px;
            }
        }
    }

    .has-errors {
        width: 20px;
        height: 20px;
        color: #EF6E0B;
    }
</style>
